/**
 * TanStack Query hooks for the RM Decision Log (Reporting page).
 *
 * There is no create/delete — one row per Solution, upserted lazily on first
 * PATCH (see src/api/decision-log.ts header). GET /api/reports/decision-log
 * returns the full (securely scoped) result set in one call — no
 * cursor/search/limit params. Sort, filter, and pagination all happen
 * client-side over that one in-memory array (see DecisionLogTable.tsx) — an
 * inline-edit PATCH never triggers a re-sort/re-filter network round trip,
 * only a cache invalidation.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import { decisionLogKeys } from "@/lib/query";
import type {
  DecisionLogEntryUpdateInput,
  DecisionLogListPage,
  DecisionLogRow,
} from "@/schemas/decision-log";

export type { DecisionLogEntryUpdateInput, DecisionLogListPage, DecisionLogRow };

export function useDecisionLog() {
  return useQuery({
    queryKey: decisionLogKeys.list(),
    queryFn: () => jsonFetch<DecisionLogListPage>("/api/reports/decision-log"),
    staleTime: 60_000,
  });
}

/**
 * No success toast — fired per-cell from inline table edits, same posture as
 * useUpdatePipelineReportEntry. The cell itself is the success feedback
 * (optimistic local value); errors still toast, and the caller rolls its
 * optimistic value back on error.
 */
export function useUpdateDecisionLogEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { solutionId: string; patch: DecisionLogEntryUpdateInput }) =>
      jsonFetch<DecisionLogRow>(`/api/reports/decision-log/${input.solutionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input.patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: decisionLogKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
